import React, { Suspense, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import { HelmetProvider } from 'react-helmet-async';
import PreviewCardPage from './pages/diagnostics/PreviewCardPage';
import DocsLayout from './features/docs/DocsLayout';

// Lazy load other pages
const PrivacyPolicy = React.lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/legal/TermsOfService'));
const CheckoutPage = React.lazy(() => import('./pages/checkout/CheckoutPage'));
const BlogPostPage = React.lazy(() => import('./pages/blog/BlogPostPage'));
const ThankYouPage = React.lazy(() => import('./pages/checkout/ThankYouPage'));
const FeaturesPage = React.lazy(() => import('./pages/FeaturesPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const FaqPage = React.lazy(() => import('./pages/FaqPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const BestDictationAppsPage = React.lazy(() => import('./pages/comparisons/BestDictationAppsPage'));
const WisprFlowAlternativePage = React.lazy(() => import('./pages/comparisons/WisprFlowAlternativePage'));
const SuperWhisperAlternativePage = React.lazy(() => import('./pages/comparisons/SuperWhisperAlternativePage'));
const WillowVoiceAlternativePage = React.lazy(() => import('./pages/comparisons/WillowVoiceAlternativePage'));
const TestimonialsPage = React.lazy(() => import('./pages/TestimonialsPage'));
const IosPage = React.lazy(() => import('./pages/ios/IosPage'));
const AffiliatePage = React.lazy(() => import('./pages/marketing/AffiliatePage'));
const SocialSharePage = React.lazy(() => import('./pages/marketing/SocialSharePage'));
const DocsHome = React.lazy(() => import('./features/docs/DocsHome'));
const DocsArticlePage = React.lazy(() => import('./features/docs/DocsArticlePage'));
const DocsCategoryPage = React.lazy(() => import('./features/docs/DocsCategoryPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Analytics component with redirection
const Analytics = () => {
  useEffect(() => {
    window.location.href = 'https://cloud.umami.is/share/CIyBmKcD2n2SW1xD/tryvoiceink.com';
  }, []);
  return null;
};

const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search]);

  return null;
};

// Declare the Affonso and DataFast global variables for TypeScript
declare global {
  interface Window {
    Affonso: {
      signup: (email: string) => void;
    };
    affonso_referral?: string;
    datafast?: (goalName: string, metadata?: { [key: string]: string }) => void;
  }
}

function App() {
  // Add Affonso tracking script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.affonso.io/js/pixel.min.js';
    script.async = true;
    script.defer = true;
    script.dataset.affonso = 'cm7zxt04w000esni7qhb66uk4';
    script.dataset.cookie_duration = '30';
    document.head.appendChild(script);

    return () => {
      // Clean up script on unmount if it exists in the DOM
      const existingScript = document.querySelector('script[src="https://cdn.affonso.io/js/pixel.min.js"]');
      if (existingScript && document.head.contains(existingScript)) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy" element={
            <Suspense fallback={<div>Loading...</div>}>
              <PrivacyPolicy />
            </Suspense>
          } />
          <Route path="/terms" element={
            <Suspense fallback={<div>Loading...</div>}>
              <TermsOfService />
            </Suspense>
          } />
          <Route path="/buy" element={
            <Suspense fallback={<div>Loading...</div>}>
              <CheckoutPage />
            </Suspense>
          } />
          <Route path="/thank-you" element={
            <Suspense fallback={<div>Loading...</div>}>
              <ThankYouPage />
            </Suspense>
          } />
          <Route path="/preview" element={<PreviewCardPage />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/blog/:slug" element={
            <Suspense fallback={<div>Loading...</div>}>
              <BlogPostPage />
            </Suspense>
          } />
          <Route path="/features" element={
            <Suspense fallback={<div>Loading...</div>}>
              <FeaturesPage />
            </Suspense>
          } />
          <Route path="/pricing" element={
            <Suspense fallback={<div>Loading...</div>}>
              <PricingPage />
            </Suspense>
          } />
          <Route path="/testimonials" element={<Navigate to="/walloflove" replace />} />
          <Route path="/faq" element={
            <Suspense fallback={<div>Loading...</div>}>
              <FaqPage />
            </Suspense>
          } />
          <Route path="/contact" element={
            <Suspense fallback={<div>Loading...</div>}>
              <ContactPage />
            </Suspense>
          } />
          <Route path="/best-dictation-apps" element={
            <Suspense fallback={<div>Loading...</div>}>
              <BestDictationAppsPage />
            </Suspense>
          } />
          <Route path="/wispr-flow-alternative" element={
            <Suspense fallback={<div>Loading...</div>}>
              <WisprFlowAlternativePage />
            </Suspense>
          } />
          <Route path="/superwhisper-alternative" element={
            <Suspense fallback={<div>Loading...</div>}>
              <SuperWhisperAlternativePage />
            </Suspense>
          } />
          <Route path="/willow-voice-alternative" element={
            <Suspense fallback={<div>Loading...</div>}>
              <WillowVoiceAlternativePage />
            </Suspense>
          } />
          <Route path="/common-issues" element={<Navigate to="/docs/common-issues" replace />} />
          <Route path="/walloflove" element={
            <Suspense fallback={<div>Loading...</div>}>
              <TestimonialsPage />
            </Suspense>
          } />
          <Route path="/ios" element={
            <Suspense fallback={<div>Loading...</div>}>
              <IosPage />
            </Suspense>
          } />
          <Route path="/recommended-models" element={<Navigate to="/docs/recommended-models" replace />} />
          <Route path="/affiliate" element={
            <Suspense fallback={<div>Loading...</div>}>
              <AffiliatePage />
            </Suspense>
          } />
          <Route path="/social-share" element={
            <Suspense fallback={<div>Loading...</div>}>
              <SocialSharePage />
            </Suspense>
          } />
          <Route path="/docs" element={
            <Suspense fallback={<div className="w-full h-screen flex items-center justify-center">Loading...</div>}>
              <DocsLayout />
            </Suspense>
          }>
            <Route index element={
              <Suspense fallback={<div className="p-8">Loading...</div>}>
                <DocsHome />
              </Suspense>
            } />
            <Route path=":slug" element={
              <Suspense fallback={<div className="p-8">Loading...</div>}>
                <DocsArticlePage />
              </Suspense>
            } />
            <Route path="category/:categoryName" element={
              <Suspense fallback={<div className="p-8">Loading...</div>}>
                <DocsCategoryPage />
              </Suspense>
            } />
          </Route>
          {/* Catch-all route for 404s */}
          <Route path="*" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
              <NotFoundPage />
            </Suspense>
          } />
        </Routes>
      </Router>
    </HelmetProvider>
  );
}

export default App;
